import { supabase } from './supabase'
import type { Payable } from './payments'
import { daysFromToday, todayIso } from './dates'

/**
 * A payment run: the moment you actually sit down and pay things.
 *
 * spec.md §12 separates three ideas that are easy to conflate — the due date
 * is when they need it, the planned date is when you decided you would do it,
 * and the run is when it happens. The run is also what sets planned dates for
 * you, which is why using runs means rarely setting one by hand.
 *
 * §13 calls resumability non-negotiable. Position is written to the database
 * after every card rather than held in React state, because "you will be
 * interrupted mid-run" is a certainty, not a risk — the phone rings, the app
 * is swiped away, and coming back to card one of seven with the totals reset
 * is how status data starts decaying.
 */

export type RunItemState = 'pending' | 'paid' | 'skipped' | 'rescheduled'

export interface RunItem {
  id: string
  instalmentId: string
  position: number
  state: RunItemState
  note: string | null
}

export interface PaymentRun {
  id: string
  spaceId: string
  label: string | null
  state: 'open' | 'done' | 'abandoned'
  position: number
  startedAt: string
  items: RunItem[]
}

/**
 * What belongs in the next run.
 *
 * Everything verified and unpaid that is either already planned for today or
 * earlier, or falls due within the week. Deliberately generous: a run you have
 * to add things to by hand is a run you stop using.
 *
 * Filtering happens here rather than in SQL because `Payable` already carries
 * the derived state, and a second definition of "payable" in another language
 * is a second thing to keep in step.
 */
export function suggestForRun(payables: Payable[], spaceId: string, horizonDays = 7): Payable[] {
  const today = todayIso()
  const horizon = daysFromToday(horizonDays)

  return payables
    .filter((p) => p.spaceId === spaceId)
    .filter((p) => p.verified)
    .filter((p) => p.state !== 'paid' && p.state !== 'cancelled')
    .filter((p) => !p.disputed)
    .filter((p) => (p.plannedDate ? p.plannedDate <= today : p.dueDate <= horizon))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
}

export async function startRun(
  spaceId: string,
  instalmentIds: string[],
  label?: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('start_payment_run', {
    p_space_id: spaceId,
    p_instalment_ids: instalmentIds,
    p_label: label ?? null,
  })
  if (error) throw error
  return data as string
}

/** The run in progress, if there is one. At most one per space is offered. */
export async function loadOpenRun(spaceId: string): Promise<PaymentRun | null> {
  const { data: runs, error } = await supabase
    .from('payment_runs')
    .select('id, space_id, label, state, position, started_at')
    .eq('space_id', spaceId)
    .eq('state', 'open')
    .is('deleted_at', null)
    .order('started_at', { ascending: false })
    .limit(1)
  if (error) throw error

  const run = runs?.[0]
  if (!run) return null

  const { data: items } = await supabase
    .from('payment_run_items')
    .select('id, instalment_id, position, state, note')
    .eq('run_id', run.id)
    .order('position')

  return {
    id: run.id as string,
    spaceId: run.space_id as string,
    label: (run.label as string) ?? null,
    state: run.state as PaymentRun['state'],
    position: Number(run.position ?? 0),
    startedAt: run.started_at as string,
    items: (items ?? []).map((i) => ({
      id: i.id as string,
      instalmentId: i.instalment_id as string,
      position: Number(i.position),
      state: i.state as RunItemState,
      note: (i.note as string) ?? null,
    })),
  }
}

/**
 * Settle one card and move on.
 *
 * Both writes happen before the screen advances. Advancing first and saving
 * afterwards would be smoother and would lose exactly the information the
 * whole feature exists to keep.
 */
export async function settleItem(
  run: PaymentRun,
  item: RunItem,
  state: Exclude<RunItemState, 'pending'>,
  note?: string,
): Promise<void> {
  const { error } = await supabase
    .from('payment_run_items')
    .update({ state, note: note ?? null })
    .eq('id', item.id)
  if (error) throw error

  await supabase
    .from('payment_runs')
    .update({ position: Math.max(run.position, item.position + 1) })
    .eq('id', run.id)
}

/** Skip keeps it in the run for later; reschedule takes it out with a new date. */
export async function rescheduleItem(
  run: PaymentRun,
  item: RunItem,
  plannedDate: string,
): Promise<void> {
  await supabase
    .from('instalments')
    .update({ planned_date: plannedDate })
    .eq('id', item.instalmentId)

  await settleItem(run, item, 'rescheduled', `verplaatst naar ${plannedDate}`)
}

export async function finishRun(run: PaymentRun, _userId?: string): Promise<void> {
  // The tally is counted in the database rather than sent from here. The
  // browser used to pass what the screen believed had happened, which is
  // exactly the number that turns out to be wrong later. See migration 0045.
  const { error } = await supabase.rpc('finish_payment_run', { p_run_id: run.id })
  if (error) throw error
}

export async function abandonRun(run: PaymentRun): Promise<void> {
  await supabase.from('payment_runs').update({ state: 'abandoned' }).eq('id', run.id)
}

/**
 * Where to resume.
 *
 * The stored position is a hint, not the truth: an item may have been settled
 * from the payments list while the run was paused. The first still-pending
 * card is what actually needs doing, so that wins.
 */
export function nextPending(run: PaymentRun): RunItem | null {
  return (
    run.items.find((i) => i.state === 'pending' && i.position >= run.position) ??
    run.items.find((i) => i.state === 'pending') ??
    null
  )
}

export interface RunSummary {
  total: number
  done: number
  paid: number
  skipped: number
  rescheduled: number
  remainingCents: number
}

export function summarise(run: PaymentRun, payables: Payable[]): RunSummary {
  const byInstalment = new Map(payables.map((p) => [p.instalmentId, p]))
  const remaining = run.items
    .filter((i) => i.state === 'pending')
    .reduce((sum, i) => {
      const p = byInstalment.get(i.instalmentId)
      return sum + (p ? p.amountCents - p.paidCents : 0)
    }, 0)

  return {
    total: run.items.length,
    done: run.items.filter((i) => i.state !== 'pending').length,
    paid: run.items.filter((i) => i.state === 'paid').length,
    skipped: run.items.filter((i) => i.state === 'skipped').length,
    rescheduled: run.items.filter((i) => i.state === 'rescheduled').length,
    remainingCents: remaining,
  }
}
